import { css, Global } from '@emotion/react';

import * as su from './styleUtils';
import * as theme from './theme';

const tailwindGlobalStyles = css`
	:root {
		-moz-tab-size: 4;
		tab-size: 4;

		-webkit-text-size-adjust: none;
		-moz-text-size-adjust: none;
		text-size-adjust: none;

		box-sizing: border-box;
	}

	*,
	*::before,
	*::after {
		box-sizing: inherit;
	}

	body {
		margin: 0;
	}

	hr {
		height: 0;
		color: inherit;
	}

	abbr[title] {
		text-decoration: underline dotted;
	}

	b,
	strong {
		font-weight: bolder;
	}

	code,
	kbd,
	samp,
	pre {
		font-family:
			ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', Menlo,
			monospace;
		font-size: 1em;
	}

	small {
		font-size: 80%;
	}

	sub,
	sup {
		font-size: 75%;
		line-height: 0;
		position: relative;
		vertical-align: baseline;
	}

	sub {
		bottom: -0.25em;
	}

	sup {
		top: -0.5em;
	}

	table {
		text-indent: 0;
		border-color: inherit;
	}

	button,
	input,
	optgroup,
	select,
	textarea {
		font-family: inherit;
		font-size: 100%;
		line-height: inherit;
		color: inherit;
		margin: 0;
		padding: 0;
	}

	button,
	select {
		text-transform: none;
	}

	button,
	[type='button'],
	[type='reset'],
	[type='submit'] {
		-webkit-appearance: button;
	}

	::-moz-focus-inner {
		border-style: none;
		padding: 0;
	}

	:-moz-focusring {
		outline: 1px dotted ButtonText;
	}

	:-moz-ui-invalid {
		box-shadow: none;
	}

	legend {
		padding: 0;
	}

	progress {
		vertical-align: baseline;
	}

	::-webkit-inner-spin-button,
	::-webkit-outer-spin-button {
		height: auto;
	}

	[type='search'] {
		-webkit-appearance: textfield;
		outline-offset: -2px;
	}

	::-webkit-search-decoration {
		-webkit-appearance: none;
	}

	::-webkit-file-upload-button {
		-webkit-appearance: button;
		font: inherit;
	}

	summary {
		display: list-item;
	}

	blockquote,
	dl,
	dd,
	h1,
	h2,
	h3,
	h4,
	h5,
	h6,
	hr,
	figure,
	p,
	pre {
		margin: 0;
	}

	button {
		background-color: transparent;
		background-image: none;
	}

	button:focus {
		outline: 1px dotted;
		outline: 5px auto -webkit-focus-ring-color;
	}

	fieldset {
		margin: 0;
		padding: 0;
	}

	ol,
	ul {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	*,
	*::before,
	*::after {
		border-width: 0;
		border-style: solid;
		border-color: currentColor;
	}

	hr {
		border-top-width: 1px;
	}

	img {
		border-style: solid;
	}

	textarea {
		resize: vertical;
	}

	input::placeholder,
	textarea::placeholder {
		color: #9ca3af;
	}

	button,
	[role='button'] {
		cursor: pointer;
	}

	table {
		border-collapse: collapse;
	}

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		font-size: inherit;
		font-weight: inherit;
	}

	a {
		color: inherit;
		text-decoration: inherit;
	}

	button,
	input,
	optgroup,
	select,
	textarea {
		padding: 0;
		line-height: inherit;
		color: inherit;
	}

	pre,
	code,
	kbd,
	samp {
		font-family:
			ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
			'Courier New', monospace;
	}

	img,
	svg,
	video,
	canvas,
	audio,
	iframe,
	embed,
	object {
		display: block;
		vertical-align: middle;
	}

	img,
	video {
		max-width: 100%;
		height: auto;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	@keyframes ping {
		75%,
		100% {
			transform: scale(2);
			opacity: 0;
		}
	}

	@keyframes pulse {
		50% {
			opacity: 0.5;
		}
	}

	@keyframes bounce {
		0%,
		100% {
			transform: translateY(-25%);
			animationtimingfunction: cubic-bezier(0.8, 0, 1, 1);
		}

		50% {
			transform: none;
			animationtimingfunction: cubic-bezier(0, 0, 0.2, 1);
		}
	}

	@media print {
		/* Remove default browser header and footer */
		@page {
			margin: 0;
		}
		body {
			padding: 30px;
		}
	}
`;

const globalStyles = css({
	/* Scrollbar */
	/* Firefox */
	'@supports not selector(::-webkit-scrollbar)': {
		'*': {
			scrollbarWidth: 'auto',
			scrollbarColor: '#cee6c1 #eeffe5',
		},
	},

	/* Chrome, Edge, and Safari */
	'*::-webkit-scrollbar': {
		width: '8px',
		height: '8px',
	},

	'*::-webkit-scrollbar-track': { background: '#eeffe5' },

	'*::-webkit-scrollbar-thumb': {
		backgroundColor: '#cee6c1',
		borderRadius: '8px',
		border: '1px none #cee6c1',
	},

	// Set the default font and text color globally.
	body: css(su.fontSans, {
		lineHeight: 1.5,
		color: theme.colors.text.primary,
		textRendering: 'geometricPrecision',
	}),

	// Chrome blocks cascade of text-rendering in these tags.
	// See https://chromium.googlesource.com/chromium/blink/+/master/Source/core/css/html.css#400
	'input, textarea, keygen, select, button': {
		textRendering: 'geometricPrecision',
	},

	/**
	 * Disable focus outlines for whole application.
	 *
	 * Needs element and pseudo-class for correct specificity.
	 */
	'*, button, input, select, textarea': {
		'&:focus': {
			outline: 'none',
		},
	},

	// Prevent the main component tree from displaying on top of popups.
	'#root': {
		isolation: 'isolate',

		// Sometimes Safari renders 3d transformed elements in a 3d space that
		// ignores z-index and stacking contexts. If there are any compositing
		// layers these layers will be rendered in the same 3d space. To prevent
		// dropdowns from rendering behind other stuff we must add this translateZ
		// to put the main page far behind all other elements.
		//
		// These bug reports are related to this issue.
		// - https://bugs.webkit.org/show_bug.cgi?id=61824
		// - https://bugs.webkit.org/show_bug.cgi?id=47125
		// - https://bugs.webkit.org/show_bug.cgi?id=224374
		//
		// This article also had some useful information.
		//   https://gwwar.com/2021/07/08/debugging-hard-things-safari-edition/
		transform: 'translateZ(-1000px)',
	},
});

/**
 * These styles may cause problems when running in Storybook.
 */
const rootLayoutStyles = css({
	body: {
		// This is needed to disable the bounce scroll effect.
		overflow: 'hidden',

		// Prevent body from scrolling by positioning it relative to the viewport.
		// `overflow: hidden` does not stop scrolling in all cases.
		position: 'fixed',
		top: 0,
		left: 0,
	},

	// The `vh` unit is broken on mobile browsers so instead it is better to use this.
	// See https://bugs.chromium.org/p/chromium/issues/detail?id=844848#c4
	'html, body, #root': {
		width: '100%',
		height: '100%',
	},

	'@media print': {
		'html, body': {
			height: 'initial !important',
			overflow: 'visible !important',
			position: 'relative',
			WebkitPrintColorAdjust: 'exact',
		},
	},

	'@page': {
		size: 'auto',
		margin: '20px 0',
	},
});

/**
 * Applies global styles.
 *
 * This component should always be rendered in every app that uses this theme.
 */
export function GlobalStyles() {
	return (
		<Global styles={[tailwindGlobalStyles, globalStyles, rootLayoutStyles]} />
	);
}
